import React, { useState, useMemo } from 'react';
import {
  defineMessages,
  FormattedMessage,
  injectIntl,
  type IntlShape,
} from 'react-intl';
import _ from 'lodash';
import type { InputProps } from 'redux-form';
import Button from 'brastrap/common/button/Button';
import Link from 'brastrap/common/link/Link';
import { filterInputProps } from 'brastrap/common/field-input/FieldInput';
import AutosuggestContainer from '../../containers/autosuggest/AutosuggestContainer';

export type SearchProps = {
  onMegaMenuClose: Function,
  active: boolean,
  focused: boolean,
  intl: IntlShape,
  onClearSearchPress: (SyntheticEvent<HTMLButtonElement>) => void,
  handleSubmit: (event?: SyntheticEvent<HTMLButtonElement>) => void,
  fields: {
    searchPhrase: InputProps,
  },
  onSearchBlur: (SyntheticEvent<HTMLInputElement>) => void,
  onSearchFocus: (SyntheticEvent<HTMLInputElement>) => void,
  onSearchKeyUp: (SyntheticEvent<HTMLInputElement>) => void,
  recentSearches: Array<{
    id: string,
    label: string,
    url: string,
  }>,
  isForMobile: boolean,
  onCloseDrawer: Function,
  onCloseModalLayer: Function,
};

const messages = defineMessages({
  searchTitle: {
    id: 'header.search.main.title',
    defaultMessage: 'Product search',
  },
  placeholder: {
    id: 'header.search.input.placeholder',
    defaultMessage: 'Search by name, code or style',
  },
  searchAction: { id: 'header.search.action', defaultMessage: 'Go' },
  recentSearches: {
    id: 'header.search.recent.title',
    defaultMessage: 'Recent searches',
  },
  recentSearchesClear: {
    id: 'header.search.recent.clear.title',
    defaultMessage: 'Clear this list',
  },
});

const Search = (props: SearchProps) => {
  const [debounceVal, setDebounceVal] = useState('');
  const [query, setQuery] = useState('');
  const [showSuggest, setShowSuggest] = useState(false);
  const displayRecentSearches = props.recentSearches.length > 0;
  let suggestionsTabIndex = -1;

  const magGlassColour = (isMobile, searchPhrase) => {
    if (!isMobile && searchPhrase !== '') {
      return '#3D3D3D';
    } else if (isMobile && searchPhrase !== '') {
      return '#FFFFFF';
    } else if (!isMobile) {
      return '#3D3D3D';
    }
    return '#FFFFFF';
  };

  if (props.active) {
    suggestionsTabIndex = 0;
  }
  const debouncedChange = useMemo(
    () =>
      _.debounce(inputValue => {
        setDebounceVal(inputValue);
        setShowSuggest(true);
      }, 500),
    []
  );
  const handleChange = e => {
    setQuery(e.target.value);
    props.fields.searchPhrase.onChange(e);
    debouncedChange(e.target.value);
  };
  const handleSubmitForm = e => {
    e.preventDefault();
    debouncedChange.cancel();
    props.handleSubmit();
    props.onMegaMenuClose();
    setShowSuggest(false);
  };
  const handleSearchAll = e => {
    props.onCloseDrawer();
    handleSubmitForm(e);
  };

  const onSearchKeyUp = e => {
    if (e.key === 'Escape') {
      props.onCloseModalLayer();
    }
  };

  return (
    <>
      <form
        onSubmit={handleSubmitForm}
        className={`c-header__search`}
        id="product-search"
        role="search"
        data-cnstrc-search-form
      >
        <fieldset className="c-header__search__main">
          <label className="u-hidden" htmlFor="product-search">
            <FormattedMessage {...messages.searchTitle} />
          </label>

          <div
            className={`${
              props.isForMobile
                ? 'c-header__search__container--mobile'
                : 'c-header__search__container'
            }`}
          >
            <input
              id={'c-header__search__input__field'}
              {...filterInputProps(props.fields.searchPhrase)}
              className={`c-header__search__input
            ${props.isForMobile ? 'c-header__search__input--mobile' : ''}`}
              onBlur={props.onSearchBlur}
              onFocus={props.onSearchFocus}
              onKeyUp={onSearchKeyUp}
              value={query}
              onChange={handleChange}
              placeholder={props.intl.formatMessage(messages.placeholder)}
              autoComplete="off"
              autoCorrect="on"
              type="search"
              aria-label="Product Search"
              data-cnstrc-search-input
            />
            <button
              onClick={() => {
                props.onCloseDrawer();
              }}
              className={`${
                props.isForMobile ? 'c-header__search-button ' : ''
              }${
                props.isForMobile && props.fields.searchPhrase.value
                  ? 'c-header__search-button--mobile'
                  : ''
              }`}
              type="submit"
              aria-label={props.intl.formatMessage(messages.searchAction)}
              data-cnstrc-search-submit-btn
            >
              <svg
                className="c-header__search__icon"
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 240 240"
                focusable="false"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  fill={magGlassColour(
                    props.isForMobile,
                    props.fields.searchPhrase.value
                  )}
                  d="M186 173l51 51c4 4 4 10 0 14-4 4-10 4-14 0L173 187c-18 15-42 25-67 25C47 211 0 163 0 106 0 48 48 0 106 0c58 0 106 48 106 106 0 25-9 49-25 67zM19 106c0-48 38-86 86-86 48 0 86 39 86 87S153 192 106 192c-48 0-86-39-86-87z"
                />
              </svg>
            </button>
          </div>
          <div
            className="c-popover c-header__search__suggestions"
            tabIndex={suggestionsTabIndex}
          >
            {displayRecentSearches && (
              <section className="c-menu">
                <h1 className="c-menu__title">
                  <FormattedMessage {...messages.recentSearches} />
                  <Button modifiers="small" onClick={props.onClearSearchPress}>
                    <FormattedMessage {...messages.recentSearchesClear} />
                  </Button>
                </h1>

                <ul className="c-menu__items">
                  {props.recentSearches.map(item => (
                    <li key={item.id} className="c-menu__item">
                      <Link
                        title={item.label}
                        className="c-menu__label"
                        href="#recentSearch"
                        rel="nofollow"
                      >
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        </fieldset>
      </form>
      {debounceVal && debounceVal.length && (
        <AutosuggestContainer
          searchTerm={debounceVal}
          showSuggest={showSuggest}
          handleSearchAll={handleSearchAll}
          setDebounceVal={setDebounceVal}
        />
      )}
    </>
  );
};

Search.defaultProps = {
  active: false,
  focused: false,
  onNavigationRequest: () => {},
  fields: { searchPhrase: {} },
  handleSubmit: () => {},
  onSearchBlur: () => {},
  onSearchFocus: () => {},
  onSearchKeyUp: () => {},
  recentSearches: [],
  suggestions: [],
};

export default injectIntl(Search);
